// src/components/WelcomeWizard.js
//
// P4-4 — Yeni kullanici 3 adimli onboarding wizard.
//
// Adimlar:
//   1) Ilgi alanlarini sec (3-6 zorunlu degil — alt 3 onerilir, atla mumkun)
//   2) Onerilen creator'lari takip et (multi-select avatar grid)
//   3) Bildirimleri ac (push permission)
//
// Tamamlama:
//   - users/{uid}.interests yazilir
//   - users/{uid}.onboardingCompletedAt = serverTimestamp()
//   - Secilen uid'ler icin follow() paralel (best-effort)
//   - Wizard kapanir, cache isaretlenir (useWelcomeWizard.close)
//
// "Atla" her adimda mumkun; final marker yine yazilir.
import React, { useCallback, useEffect, useMemo, useState, useRef } from "react";
import useFocusTrap from "../hooks/useFocusTrap";
import { useTranslation, Trans } from "react-i18next";
import { auth, db } from "../firebase";
import { doc, serverTimestamp, setDoc } from "firebase/firestore";
import { SELECTABLE_CATEGORIES } from "../myclipsv3/feed/feedCategories";
import {
  ONBOARDING_MIN_INTERESTS,
  ONBOARDING_MAX_INTERESTS,
} from "../constants/myClipsSchema";
import { fetchTrendingCreators } from "../myclipsv3/discovery/trendingCreatorsClient";
import { follow } from "../services/relationships";
import ResponsiveImage from "./ResponsiveImage";
import { showToast } from "../ToastBoot";
import { boxImageFor } from "./Labubu/BoxTile";
import useBodyScrollLock from "../hooks/useBodyScrollLock";
import { useAdressizKatmanGeri } from "../app/useAdressizKatmanGeri";
import { ADRESSIZ_ETIKET } from "../app/adressizKatmanGeri";
import "./WelcomeWizard.css";
import { IkonKoprusu } from "../icon-system/runtime/IkonKoprusu";

// EMİR #78 — push-izni adımı KALDIRILDI (değer-sonrası soft-prompt'a taşındı:
// FirstSparkPushPrompt). Yerine 3 hafif "döngü öğretimi" ekranı (atlanabilir):
// Puanla (5★) → Üret & Kazan → MyCards. Gizli eşik/oran SIZDIRILMAZ.
const STEPS = ["interests", "follows", "rate", "create", "mycards"];
const SUGGEST_FETCH_LIMIT = 24;

/**
 * Trend üretici satırı — şekil `myclipsv3/discovery/trendingCreatorsClient`
 * `fetchTrendingCreators` GÖVDESİNDEN TÜRETİLDİ (elle yazılmadı).
 */
type TrendUretici = Awaited<ReturnType<typeof fetchTrendingCreators>>[number];

function ProgressDots({ stepIdx }: { stepIdx: number }) {
  return (
    <div className="ww-dots" aria-hidden="true">
      {STEPS.map((s, i) => (
        <span key={s} className={`ww-dot${i === stepIdx ? " ww-dot--on" : ""}${i < stepIdx ? " ww-dot--done" : ""}`} />
      ))}
    </div>
  );
}

function StepInterests({ selected, onToggle }: { selected: Set<string>; onToggle: (id: string) => void }) {
  const count = selected.size;
  return (
    <div className="ww-step">
      <h2 className="ww-title">Hoş geldin <IkonKoprusu aile="social.wave" eski="👋" /></h2>
      <p className="ww-sub">
        İlgi alanlarını seç. Akış senin için kişiselleşsin.
      </p>
      <div className="ww-chip-grid" role="group" aria-label="İlgi alanları">
        {SELECTABLE_CATEGORIES.map((cat) => {
          const on = selected.has(cat.id);
          return (
            <button
              key={cat.id}
              type="button"
              className={`ww-chip${on ? " ww-chip--on" : ""} pressable`}
              onClick={() => onToggle(cat.id)}
              aria-pressed={on}
            >
              {cat.label}
            </button>
          );
        })}
      </div>
      <div className="ww-meta">
        {count >= ONBOARDING_MIN_INTERESTS
          ? `${count} seçildi`
          : `En az ${ONBOARDING_MIN_INTERESTS} önerilir · ${count}/${ONBOARDING_MAX_INTERESTS}`}
      </div>
    </div>
  );
}

function StepFollows({ creators, loading, selected, onToggle }: { creators: TrendUretici[]; loading: boolean; selected: Set<string>; onToggle: (uid: string) => void }) {
  return (
    <div className="ww-step">
      <h2 className="ww-title">Birkaç hesabı keşfet</h2>
      <p className="ww-sub">Şimdi takip et, akışında onlara yer ayır.</p>
      {loading ? (
        <div className="ww-empty">Yükleniyor…</div>
      ) : creators.length === 0 ? (
        <div className="ww-empty">Şimdilik önerilen hesap yok. Atla.</div>
      ) : (
        <div className="ww-creators" role="group" aria-label="Önerilen hesaplar">
          {creators.map((c) => {
            const on = selected.has(c.uid);
            const username = c.username || c.displayName || c.uid.slice(0, 8);
            return (
              <button
                key={c.uid}
                type="button"
                className={`ww-creator${on ? " ww-creator--on" : ""} pressable`}
                onClick={() => onToggle(c.uid)}
                aria-pressed={on}
              >
                <ResponsiveImage
                  className="ww-creator-avatar"
                  src={c.photoURL}
                  widths={[160]}
                  fallback="/avatars/default.svg"
                  alt=""
                />
                <span className="ww-creator-name" title={username}>
                  {username}
                </span>
                <span className="ww-creator-action">
                  {on ? "Takipte" : "Takip Et"}
                </span>
              </button>
            );
          })}
        </div>
      )}
    </div>
  );
}

// EMİR #78 — DÖNGÜ ÖĞRETİMİ ekran-1: Puanla (çift-dokun=5★; kalp YOK, yıldız var).
// EMİR #79-C — i18n: <strong> gömülü → Trans (piksel-aynı DOM).
function StepRate() {
  const { t } = useTranslation(["onboarding"]);
  return (
    <div className="ww-step ww-step--teach">
      <div className="ww-teach-illu ww-teach-rate" aria-hidden="true">
        <span className="ww-teach-star">★</span>
        <span className="ww-teach-tap">👆</span>
      </div>
      <h2 className="ww-title">{t("onboarding:rate.title")}</h2>
      <p className="ww-sub">
        <Trans i18nKey="onboarding:rate.sub" components={[<strong />]} />
      </p>
    </div>
  );
}

// EMİR #78 — DÖNGÜ ÖĞRETİMİ ekran-2: Üret & Kazan (5★ topla → yıldızın büyür).
function StepCreate() {
  const { t } = useTranslation(["onboarding"]);
  return (
    <div className="ww-step ww-step--teach">
      <div className="ww-teach-illu" aria-hidden="true">🎬✨</div>
      <h2 className="ww-title">{t("onboarding:create.title")}</h2>
      <p className="ww-sub">{t("onboarding:create.sub")}</p>
    </div>
  );
}

// EMİR #78 — DÖNGÜ ÖĞRETİMİ ekran-3: MyCards (kaliteli katkı kutu düşürür).
function StepMyCards() {
  const { t } = useTranslation(["onboarding"]);
  return (
    <div className="ww-step ww-step--teach">
      <img className="ww-teach-box" src={boxImageFor()} alt="" aria-hidden="true" />
      <h2 className="ww-title">{t("onboarding:mycardsIntro.title")}</h2>
      <p className="ww-sub">{t("onboarding:mycardsIntro.sub")}</p>
    </div>
  );
}

/* EMİR #85.2e — lokal kopya kaldırıldı; merkezî useBodyScrollLock kullanılıyor
   (iOS'ta overflow:hidden arka sayfayı kilitlemez; hook position:fixed tekniği). */

interface WelcomeWizardProps {
  open?: boolean;
  onClose?: () => void;
  /**
   * İLK ADIMDA GERİ — sihirbazdan çık ama KALICI KAPATMA.
   *
   * `onClose` (`useWelcomeWizard.close`) localStorage'a `"done"` yazar ve
   * sihirbaz BİR DAHA AÇILMAZ; geri tuşunu ona bağlamak kazara bir jestle
   * onboarding'i kalıcı öldürmek olurdu. Bu prop `useWelcomeWizard.gizle`ye
   * bağlanır (yalnız bu oturumda gizler). Verilmezse geri tuşu ilk adımda
   * hiçbir şey yapmaz — sessizce `onClose`a DÜŞMEZ (yanlış tarafa düşen bir
   * varsayılan, tam olarak kaçındığımız kalıcı kapatmayı geri getirirdi).
   */
  onGeriCik?: () => void;
}

export default function WelcomeWizard({ open, onClose, onGeriCik }: WelcomeWizardProps) {
  // Rıza denetimi — modal açıkken arka plan kaymasın
  // eslint-disable-next-line react-hooks/rules-of-hooks
  useBodyScrollLock(!!open);
  const [stepIdx, setStepIdx] = useState(0);
  const [interests, setInterests] = useState<Set<string>>(() => new Set());
  const [creators, setCreators] = useState<TrendUretici[]>([]);
  const [loadingCreators, setLoadingCreators] = useState(false);
  // `<string>`: küme uid tutuyor (`toggleCreator` → `next.add(uid)`) ve
  // `follow(tUid)` string bekliyor. Annotasyonsuz `new Set()` `Set<unknown>`
  // çıkarıyor → `Array.from(...)` `unknown[]` olup GEVŞEK kapıyı kırıyordu.
  // (Tip-yalnız; `services/relationships.follow` tiplenince ortaya çıktı.)
  const [selectedCreators, setSelectedCreators] = useState(() => new Set<string>());
  const [finalizing, setFinalizing] = useState(false);
  const { t } = useTranslation(["common"]);

  // Açıldığında reset
  useEffect(() => {
    if (!open) return;
    setStepIdx(0);
    setInterests(new Set());
    setSelectedCreators(new Set());
  }, [open]);

  // ══ 🔙 GERİ TUŞU = ÖNCEKİ ADIM (adres YOK, girdi VAR) ══════════════════
  // ÖLÇÜLDÜ (2026-09-01): bu bileşen `history`ye HİÇ dokunmuyordu
  // (`grep -c 'pushState\|popstate'` → 0). Yani Android donanım geri tuşu
  // sihirbazı değil ALTTAKİ sayfayı tüketiyordu — `ADRESSIZ_ETIKET`
  // başlığındaki beş mlv3 katmanının aynısı, altıncı yüzeyde.
  //
  // ⚠️ GERİ ≠ KAPAT (önceki turun bilerek ertelediği kusur, gerekçesi sağlam):
  // `onClose` cache'e "done" yazar → sihirbaz BİR DAHA AÇILMAZ. Geri tuşuna
  // "kapat" demek, kazara bir jestle onboarding'i KALICI öldürmek olurdu.
  // Bu yüzden anlam ADIM düzeyinde: geri = önceki adım; İLK adımda geri =
  // `onGeriCik` (kalıcı kapatma YOK, bkz. `useWelcomeWizard.gizle`).
  //
  // ⚠️ Adres YAZILMAZ: sihirbazın `resolvePathToPage`te karşılığı olan bir
  // yolu yok; `/welcome/2` gibi bir adres soğuk yüklemede o adımı AÇMAZ →
  // yalan söyleyen adres olurdu (`adressizKatmanGeri.ts` gerekçesi).
  const stepIdxRef = useRef(stepIdx);
  stepIdxRef.current = stepIdx;
  const geriAdim = useCallback(() => {
    // ⚠️ Idempotent olmalı (kanca sözleşmesi): kendi `back()`imizden dönen
    // pop'ta da çağrılır. `setStepIdx` ve `onGeriCik` ikisi de idempotent.
    if (stepIdxRef.current <= 0) {
      onGeriCik && onGeriCik();
      return;
    }
    setStepIdx((s) => Math.max(0, s - 1));
  }, [onGeriCik]);
  const { girdiyiYenidenAl } = useAdressizKatmanGeri({
    acik: !!open,
    etiket: ADRESSIZ_ETIKET.karsilamaSihirbazi,
    onKapat: geriAdim,
  });
  // Her adım KENDİ girdisini ister: geri tuşu girdiyi tüketir, sonraki adım
  // için tazesi alınır. Olmasaydı kusur BİR BASIŞ GEÇ görünürdü — ilk geri
  // çalışır, ikincisi alttaki sayfayı yer.
  useEffect(() => {
    if (!open) return;
    girdiyiYenidenAl();
  }, [open, stepIdx, girdiyiYenidenAl]);

  // Step 2'ye geçince trending creators fetch
  useEffect(() => {
    if (!open || stepIdx !== 1) return;
    let alive = true;
    setLoadingCreators(true);
    (async () => {
      try {
        const list = await fetchTrendingCreators({ limit: SUGGEST_FETCH_LIMIT });
        if (!alive) return;
        // Self'i defansif olarak filtrele.
        const me = auth.currentUser?.uid;
        setCreators(me ? list.filter((c) => c.uid !== me) : list);
      } catch (_) {
        if (alive) setCreators([]);
      } finally {
        if (alive) setLoadingCreators(false);
      }
    })();
    return () => {
      alive = false;
    };
  }, [open, stepIdx]);

  const toggleInterest = useCallback((id: string) => {
    setInterests((prev) => {
      const next = new Set(prev);
      if (next.has(id)) {
        next.delete(id);
      } else {
        if (next.size >= ONBOARDING_MAX_INTERESTS) return prev;
        next.add(id);
      }
      return next;
    });
  }, []);

  const toggleCreator = useCallback((uid: string) => {
    setSelectedCreators((prev) => {
      const next = new Set(prev);
      if (next.has(uid)) next.delete(uid);
      else next.add(uid);
      return next;
    });
  }, []);

  const finalize = useCallback(async () => {
    if (finalizing) return;
    setFinalizing(true);
    const uid = auth.currentUser?.uid || null;
    try {
      if (uid) {
        // İnterests + completion marker (tek write)
        const payload: Record<string, any> = {
          onboardingCompletedAt: serverTimestamp(),
        };
        if (interests.size > 0) {
          payload.interests = Array.from(interests);
        }
        try {
          await setDoc(doc(db, "users", uid), payload, { merge: true });
        } catch (_) {
          // marker yazımı best-effort
        }
        // Follow'lar paralel best-effort
        const followPromises = Array.from(selectedCreators).map((tUid) =>
          follow(tUid).catch(() => {
            /* sessiz */
          }),
        );
        await Promise.all(followPromises);
      }
      try {
        showToast("Hazırız!", { variant: "success" });
      } catch (_) {}
    } finally {
      setFinalizing(false);
      onClose && onClose();
    }
  }, [finalizing, interests, selectedCreators, onClose]);

  const isLast = stepIdx === STEPS.length - 1;

  const advance = useCallback(async () => {
    // EMİR #78 — push-izni ARTIK BURADA İSTENMİYOR (değer-anına taşındı). Son
    // ekran (MyCards) → finalize; aksi → sonraki ekran.
    if (stepIdx === STEPS.length - 1) {
      await finalize();
      return;
    }
    setStepIdx((s) => Math.min(STEPS.length - 1, s + 1));
  }, [stepIdx, finalize]);

  const skip = useCallback(() => {
    if (stepIdx === STEPS.length - 1) {
      finalize();
      return;
    }
    setStepIdx((s) => Math.min(STEPS.length - 1, s + 1));
  }, [stepIdx, finalize]);

  const stepLabel = useMemo(() => {
    if (isLast) return t("common:finish");
    if (stepIdx === 1 && selectedCreators.size > 0) {
      return `Takip Et (${selectedCreators.size})`;
    }
    return t("common:continue");
  }, [isLast, stepIdx, selectedCreators.size, t]);

  // EMİR #98 Dalga-2 (a) — odak hapsi + beyan TEK KAYNAKTAN.
  // ⚠️ YAPISAL GÜVENCE: `ref` ile `{...katman}` AYNI elemanda. Kancanın en
  // sinsi kusuru "containerRef yanlış öğeyi gösteriyor"du — beyan ile hapsin
  // ayrı elemanlara yazılabilmesinden doğuyordu. Aynı yere yazılınca o kusur
  // kodlanamaz hâle gelir; ayrıca ayırmak için birini SİLMEK gerekir, ki o da
  // testte görünür.
  const hapisRef = useRef<HTMLDivElement | null>(null);
  const katman = useFocusTrap({ open: open, onClose, containerRef: hapisRef });

  if (!open) return null;

  return (
    <div ref={hapisRef} {...katman} className="ww-overlay" aria-label="Hoş geldin">
      <div className="ww-panel">
        <header className="ww-header">
          <ProgressDots stepIdx={stepIdx} />
        </header>

        <div className="ww-body">
          {stepIdx === 0 && (
            <StepInterests selected={interests} onToggle={toggleInterest} />
          )}
          {stepIdx === 1 && (
            <StepFollows
              creators={creators}
              loading={loadingCreators}
              selected={selectedCreators}
              onToggle={toggleCreator}
            />
          )}
          {stepIdx === 2 && <StepRate />}
          {stepIdx === 3 && <StepCreate />}
          {stepIdx === 4 && <StepMyCards />}
        </div>

        <footer className="ww-actions">
          <button
            type="button"
            className="ww-btn ww-btn-skip pressable"
            onClick={skip}
            disabled={finalizing}
          >
            {isLast ? t("common:later") : t("common:skip")}
          </button>
          <button
            type="button"
            className="ww-btn ww-btn-primary pressable"
            onClick={advance}
            disabled={finalizing}
          >
            {finalizing ? "…" : stepLabel}
          </button>
        </footer>
      </div>
    </div>
  );
}
